import {Digits} from './constants';
import {HumanReadableFormat} from './human-readable/HumanReadableFormat';
import {MoneyFormat} from './money';
import {NumberFormat} from './number';
import {SequenceFormat} from './sequence';

/**
 * @description Formatter for integers
 * @type {NumberFormat}
 * @example
 * // returns '1'
 * INTEGER.format(1.3);
 * // returns '2'
 * INTEGER.format(1.5);
 */
export const INTEGER: NumberFormat = new NumberFormat(0, true);

/**
 * @description Formatter of floats with a precision in 2 decimals that doesn't allow 0
 * @type {NumberFormat}
 * @example
 * // returns '0.51'
 * INTEGER.format(0.512);
 * // returns ''
 * FLOAT.format(0);
 */
export const FLOAT: NumberFormat = new NumberFormat(2, false);

/**
 * @description Formatter for floats with a precision of 1 decimal.
 * @type {NumberFormat}
 * @example
 * // returns '0.5'
 * RATIO.format(0.52);
 */
export const RATIO: NumberFormat = new NumberFormat(1, true);

/**
 * @description Formatter for floats with a precision of 2 decimal and positive values only.
 * @type {NumberFormat}
 * @example
 * // returns '0.51'
 * FLOAT_FINANCIAL.format(0.512);
 * // returns '(0.51)'
 * FLOAT_FINANCIAL.format(-0.512);
 */
export const FLOAT_FINANCIAL: NumberFormat = new NumberFormat(2, true, true); // FLOAT_PAREN FLOAT_FINANCIAL

/**
 * @description Formatter for floats with a precision of 2 decimal and percent suffix.
 * @type {NumberFormat}
 * @example
 * // returns '-20.51%'
 * PERCENT.format(-20.512);
 */
export const PERCENT: NumberFormat = new NumberFormat(2, true, false, '%');

/**
 * @description Formatter for floats with a precision of 1 decimal and percent suffix.
 * @type {NumberFormat}
 * @example
 * // returns '10.5%'
 * PERCENT_FINANCIAL.format(10.512);
 */
export const PERCENT_FINANCIAL: NumberFormat = new NumberFormat(
    1,
    true,
    false,
    '%',
);

/**
 * @description Formatter for floats with a precision in 2 decimals.
 * @type {NumberFormat}
 * @example
 * // returns '0.51'
 * FORCED_FLOAT.format(0.512);
 * // returns '0'
 * FORCED_FLOAT.format(0);
 */
export const FORCED_FLOAT: NumberFormat = new NumberFormat(2, true, false);

/**
 * @description Money formatter that doesn't allow an amount of 0
 * @type {MoneyFormat}
 * @example
 * const locale = {
        currency: 'USD',
        symbolPosition: 'LEFT',
        thousandFormat: 'COMMA',
        decimalFormat: 'PERIOD'
    };

 * // returns ''
 * MONEY.format({amount: 0}, locale);
 * // returns '$1,500.45'
 * MONEY.format({amount: 1500.45}, locale);
 */
export const MONEY: MoneyFormat = new MoneyFormat(new NumberFormat(2, false));

/**
 * @description Money formatter that allowes an amount of 0.
 * @type {MoneyFormat}
 * @example
 * const locale = {
        currency: 'USD',
        symbolPosition: 'LEFT',
        thousandFormat: 'COMMA',
        decimalFormat: 'PERIOD'
    };

 * // returns '$0.00'
 * MONEY_WITH_ZERO.format({amount: 0}, locale, {showZeros});
 */
export const MONEY_WITH_ZERO: MoneyFormat = new MoneyFormat(
    new NumberFormat(2, true),
);

/**
 * @description Money formatter that doesn't show decimals and doesn't allow an amount of 0
 * @type {MoneyFormat}
 * @example
 * const locale = {
        currency: 'USD',
        symbolPosition: 'LEFT',
        thousandFormat: 'COMMA',
        decimalFormat: 'PERIOD'
    };

 * // returns ''
 * MONEY.format({amount: 0}, locale);
 * // returns '$1,500'
 * MONEY_NO_DECIMALS.format({amount: 1500.45}, locale);
 */
export const MONEY_NO_DECIMALS: MoneyFormat = new MoneyFormat(
    new NumberFormat(0, false),
);

/**
 * @description Money formatter that doesn't show decimals and allowes an amount of 0
 * @type {MoneyFormat}
 * @example
 * const locale = {
        currency: 'USD',
        symbolPosition: 'LEFT',
        thousandFormat: 'COMMA',
        decimalFormat: 'PERIOD'
    };

 * // returns '$0'
 * MONEY.format({amount: 0}, locale,  {showZeros: true});
 * // returns '$1,500'
 * MONEY_NO_DECIMALS_WITH_ZERO.format({amount: 1500.45}, locale);
 */
export const MONEY_NO_DECIMALS_WITH_ZERO: MoneyFormat = new MoneyFormat(
    new NumberFormat(0, true),
);

/**
 * @description Money formatter for par of currency
 * @type {MoneyFormat}
 * @example
 * const locale = {
        currency: 'USD',
        symbolPosition: 'LEFT',
        thousandFormat: 'COMMA',
        decimalFormat: 'PERIOD'
    };

 * // returns '$1.450000'
 * PAR_VALUE.format({amount: 1.45}, locale);
 */
export const PAR_VALUE: MoneyFormat = new MoneyFormat(
    new NumberFormat(6, true),
);

/**
 * @description Money formatter that formats amount to millions and doesn't allow 0
 * @type {MoneyFormat}
 * @example
 * const locale = {
        currency: 'USD',
        symbolPosition: 'LEFT',
        thousandFormat: 'COMMA',
        decimalFormat: 'PERIOD'
    };

 * // returns ''
 * MONEY.format({amount: 0}, locale);
 * // returns '$0.14M'
 * MONEY_M.format({amount: 145000}, locale);
 */
export const MONEY_M: MoneyFormat = new MoneyFormat(
    new NumberFormat(2, false),
    Digits.MILLION,
);

/**
 * @description Money formatter that formats amount to millions and doesn't allow 0
 * @type {MoneyFormat}
 * @example
 * const locale = {
        currency: 'USD',
        symbolPosition: 'LEFT',
        thousandFormat: 'COMMA',
        decimalFormat: 'PERIOD'
    };

 * // returns '$0.00'
 * MONEY.format({amount: 0}, locale,  {showZeros: true});
 * // returns '$0.14M'
 * MONEY_M_WITH_ZERO.format({amount: 145000}, locale);
 */
export const MONEY_M_WITH_ZERO: MoneyFormat = new MoneyFormat(
    new NumberFormat(2, true),
    Digits.MILLION,
);

/**
 * @description Money formatter that formats amount to thousands and doesn't allow 0
 * @type {MoneyFormat}
 * @example
 * const locale = {
        currency: 'USD',
        symbolPosition: 'LEFT',
        thousandFormat: 'COMMA',
        decimalFormat: 'PERIOD'
    };

 * // returns ''
 * MONEY.format({amount: 0}, locale);
 * // returns '$14K'
 * MONEY_T.format({amount: 14400}, locale);
 */
export const MONEY_T: MoneyFormat = new MoneyFormat(
    new NumberFormat(2, false),
    Digits.THOUSAND,
);

/**
 * @description Money formatter that formats amount to thousands and allows 0
 * @type {MoneyFormat}
 * @example
 * const locale = {
        currency: 'USD',
        symbolPosition: 'LEFT',
        thousandFormat: 'COMMA',
        decimalFormat: 'PERIOD'
    };

 * // returns '$0.00'
 * MONEY.format({amount: 0}, locale, {showZeros: true});
 * // returns '$14K'
 * MONEY_T_WITH_ZERO.format({amount: 14400}, locale);
 */
export const MONEY_T_WITH_ZERO: MoneyFormat = new MoneyFormat(
    new NumberFormat(2, true),
    Digits.THOUSAND,
);

/**
 * @description Money formatter for currency rate that has a precision of 4 decimals and doesn't allow 0
 * @type {MoneyFormat}
 * @example
 * const locale = {
        currency: 'USD',
        symbolPosition: 'LEFT',
        thousandFormat: 'COMMA',
        decimalFormat: 'PERIOD'
    };
 *
 * // returns '$14.6743'
 * MONEY_CURRENCY_RATE.format({amount: 14.67432}, locale);
 */
export const MONEY_CURRENCY_RATE: MoneyFormat = new MoneyFormat(
    new NumberFormat(4, false),
);

/**
 * @description Money formatter with a precision of 1 decimal that doesn't allow 0
 * @type {MoneyFormat}
 * @example
 * const locale = {
        currency: 'USD',
        symbolPosition: 'LEFT',
        thousandFormat: 'COMMA',
        decimalFormat: 'PERIOD'
    };
 *
 * // returns ''
 * MONEY.format({amount: 0}, locale);
 * // returns '$14.7'
 * MONEY_PRECISE.format({amount: 14.67432}, locale);
 */
export const MONEY_PRECISE: MoneyFormat = new MoneyFormat(FLOAT); // MONEY_M_PRECISE MONEY_PRECISE

/**
 * @description formatter for multiples
 * @type {MoneyFormat}
 * @example
 * const locale = {
        currency: 'USD',
        symbolPosition: 'LEFT',
        thousandFormat: 'COMMA',
        decimalFormat: 'PERIOD'
    };
 *
 * // returns '14.67x'
 * MULTIPLES.format({amount: 14.67432}, locale);
 */
export const MULTIPLES: NumberFormat = new NumberFormat(2, true, false, 'x'); // RETURNS_MULTIPLES

/**
 * @description formatter for multiples without suffix
 * @type {MoneyFormat}
 * @example
 *
 * // returns '-14.67'
 * MULTIPLES.format(-14.67432);
 */
export const MULTIPLES_WITHOUT_SUFFIX: NumberFormat = new NumberFormat(
    2,
    true,
    false,
);

/**
 * @description formatter for a tooltip
 * @type {MoneyFormat}
 * @example
 * const locale = {
        currency: 'USD',
        symbolPosition: 'LEFT',
        thousandFormat: 'COMMA',
        decimalFormat: 'PERIOD'
    };
 *
 * // returns ''
 * TOOLTIP.format(0);
 * // returns '14.6743'
 * TOOLTIP.format(14.67432);
 */
export const TOOLTIP: NumberFormat = new NumberFormat(4, false);

/**
 * @description add sequence suffix to a number
 * @type {NumberFormat}
 * @example
 *
 * // returns '14th'
 * SEQUENCE_NUMBER.format(14);
 */
export const SEQUENCE_NUMBER: SequenceFormat = new SequenceFormat(0, true);
export const HUMAN_READABLE: HumanReadableFormat = new HumanReadableFormat();
